/**
 * A date with no time — `yyyy-MM-dd` — as the day it names, never as an instant.
 *
 * survey-api sends the SEC ADV register's dates this way (`generatedOn`, a filing month, a
 * filing's own date): a `DateOnly`, which names a day on the SEC's calendar and carries no zone.
 * `new Date('2026-09-24')` reads that string as **UTC midnight**, and `formatDateTime` then
 * shows it in the viewer's zone — the evening before, anywhere west of Greenwich. So the day is
 * built and formatted in UTC alike, which puts it back exactly as sent, wherever the reader is.
 *
 * Display, not validation: a value that does not read as a real day of that spelling is shown
 * as it came, never refused or corrected.
 */

const DAY: Intl.DateTimeFormatOptions = {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
  year: 'numeric'
};

const MONTH: Intl.DateTimeFormatOptions = { timeZone: 'UTC', month: 'long', year: 'numeric' };

/** The UTC midnight of `yyyy-MM-dd`, or null when the value is not that spelling of a real
 *  day — checked by writing the day back out, so `2026-02-30` is not quietly March 2nd. */
export function utcDay(value: string): Nullable<Date> {
  const [year, month, day] = value.split('-').map((part) => Number.parseInt(part, 10));
  if (year === undefined || month === undefined || day === undefined) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10) === value ? date : null;
}

/** `2026-09-24` as `Sep 24, 2026` — `formatDateTime`'s spelling, without the time it does not
 *  have. */
export function formatDay(value: string): string {
  return utcDay(value)?.toLocaleDateString('en-US', DAY) ?? value;
}

/** A month named by its first day — `2025-06-01` as `June 2025`. */
export function formatMonth(value: string): string {
  return utcDay(value)?.toLocaleDateString('en-US', MONTH) ?? value;
}
